import { Button, Icon, IconButton, Stack, Typography } from '@mui/material';
import type { InvalidRow, RowIssue, ValidationResult } from '@schooly/components/xls-service';
import {
  ArrowLeftIcon,
  Attention2Icon,
  CrossIcon,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalLarge,
  ModalMain,
  PublishIcon,
  Spin,
  TabsWithCounters,
  TypographyWithOverflowHint,
  VirtualizedList,
} from '@schooly/style';
import { FC, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { buildTestId, TIMETABLE } from '@schooly/constants/test-ids';
import { isNotFalse } from '@schooly/utils/predicates';

import { getImportableRows } from './helpers/validateTimetableFile';
import { useTimetableImportMessages } from './helpers/useTimetableImportMessages';

enum Tab {
  Errors,
  Warnings,
}

type InconsistenciesModalProps = {
  validationResult: ValidationResult;
  onBack: () => void;
  onImport: () => void;
  isImporting: boolean;
  onClose: () => void;
};

export const InconsistenciesModal: FC<InconsistenciesModalProps> = ({
  validationResult,
  onBack,
  onImport,
  isImporting,
  onClose,
}) => {
  const { formatMessage } = useIntl();
  const [activeTab, setActiveTab] = useState(Tab.Errors);

  const { errorRows, warningRows, importableCount } = useMemo(() => {
    return {
      errorRows: validationResult.invalidRows.filter((r) =>
        r.issues.some((i) => i.type === 'error'),
      ),
      warningRows: validationResult.invalidRows.filter((r) =>
        r.issues.some((i) => i.type === 'warning'),
      ),
      importableCount: getImportableRows(validationResult).length,
    };
  }, [validationResult]);

  const tabItems =
    [
      errorRows.length > 0 && {
        labelId: 'import-Timetable-InconsistenciesTabs-Errors',
        value: Tab.Errors,
        count: errorRows.length,
        icon: TabIcon(Tab.Errors),
        rows: errorRows,
        tooltipContent: formatMessage({ id: 'import-Timetable-InconsistenciesTabs-ErrorsTooltip' }),
        tabTestId: TIMETABLE.INCONSISTENCIES_TAB_ERRORS,
      },
      warningRows.length > 0 && {
        labelId: 'import-Timetable-InconsistenciesTabs-Warnings',
        value: Tab.Warnings,
        count: warningRows.length,
        icon: TabIcon(Tab.Warnings),
        rows: warningRows,
        tooltipContent: formatMessage({
          id: 'import-Timetable-InconsistenciesTabs-WarningsTooltip',
        }),
        tabTestId: TIMETABLE.INCONSISTENCIES_TAB_WARNINGS,
      },
    ].filter(isNotFalse) ?? [];

  const activeRows = tabItems[activeTab]?.rows ?? [];

  return (
    <ModalLarge open onClose={onClose}>
      <ModalHeader
        multiline
        sx={{ pb: 0 }}
        title={
          <Stack gap={0.25}>
            <Typography variant="h1">
              {formatMessage({ id: 'import-Timetable-Inconsistencies' })}
            </Typography>
            <Typography variant="h3" color="common.grey2">
              {formatMessage({ id: 'import-Timetable-InconsistenciesDescription' })}
            </Typography>

            <TabsWithCounters
              tabItems={tabItems}
              onChange={(_, v: number) => setActiveTab(v)}
              activeTab={activeTab}
              sx={{
                pt: 3.25,
                '& .MuiTabs-flexContainer': { overflow: 'scroll' },
                mb: 0,
                '& .MuiTab-root': {
                  height: 28,
                  minHeight: 0,
                  '&:last-child': { ml: 2.75 },
                },
              }}
              textVariant="h2"
            />
          </Stack>
        }
        active
      >
        <IconButton onClick={onClose} sx={{ pt: 2.75 }}>
          <CrossIcon />
        </IconButton>
      </ModalHeader>

      <ModalMain>
        <ModalContent active>
          <Stack height="100%" sx={{ maxWidth: '100%' }}>
            {activeRows.length > 0 && <RowsTable rows={activeRows} activeTab={activeTab} />}
          </Stack>
        </ModalContent>
      </ModalMain>

      <ModalFooter active>
        <Stack direction="row" justifyContent="space-between" width="100%">
          <Button variant="outlined" startIcon={<ArrowLeftIcon />} onClick={onBack}>
            {formatMessage({ id: 'action-Back' })}
          </Button>
          <Button
            variant="contained"
            disabled={importableCount === 0 || isImporting}
            onClick={onImport}
            sx={(theme) => ({
              '& .svg-icon path:last-child': { fill: theme.palette.primary.main },
              '&.Mui-disabled .svg-icon path:last-child': { fill: theme.palette.common.grey },
            })}
            startIcon={isImporting ? <Spin /> : <PublishIcon className="reset-svg-currentColor" />}
          >
            {formatMessage({ id: 'exports-ImportButton' })}
            {` ${formatMessage({ id: 'import-Timetable-RowsCount' }, { count: importableCount })}`}
          </Button>
        </Stack>
      </ModalFooter>
    </ModalLarge>
  );
};

const COL_SX = { width: 118, flexShrink: 0, px: 1 } as const;
const COL_ROOM_SX = { width: 222, flexShrink: 0, px: 1 } as const;
const COL_ERROR_SX = { flex: 1, minWidth: 0, px: 1 } as const;

const getColSx = (col: string) => (col === 'room' ? COL_ROOM_SX : COL_SX);

type RowsTableProps = {
  rows: InvalidRow[];
  activeTab: Tab;
};

const RowsTable: FC<RowsTableProps> = ({ rows, activeTab }) => {
  const columns = Object.keys(rows[0].data);
  const hasRoomColumn = columns.includes('room');
  const msg = useTimetableImportMessages();

  const getColor = (issue: RowIssue) => {
    if (issue.type === 'error' && activeTab === Tab.Errors) return 'error.main';
    if (issue.type === 'warning' && activeTab === Tab.Warnings) return 'common.orange';
    return 'common.grey2';
  };

  return (
    <Stack
      data-test-id={TIMETABLE.INCONSISTENCIES_TABLE}
      sx={{ flex: 1, overflow: 'hidden', minWidth: 850 }}
    >
      <Stack
        direction="row"
        alignItems="center"
        sx={{ py: 1, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
      >
        <Typography noWrap color="common.grey" sx={COL_SX}>
          {msg.columnRow}
        </Typography>
        <Typography noWrap color="common.grey" sx={COL_SX}>
          {msg.columnDay}
        </Typography>
        <Typography noWrap color="common.grey" sx={COL_SX}>
          {msg.columnPeriod}
        </Typography>
        <Typography noWrap color="common.grey" sx={COL_SX}>
          {msg.columnGroup}
        </Typography>
        {hasRoomColumn && (
          <Typography noWrap color="common.grey" sx={COL_ROOM_SX}>
            {msg.columnRoom}
          </Typography>
        )}
        <Typography noWrap color="common.grey" sx={COL_ERROR_SX}>
          {msg.columnError}
        </Typography>
      </Stack>
      <VirtualizedList
        items={rows}
        renderItem={(_, row) => {
          const issuesByCol = new Map(row.issues.map((i) => [i.column, i]));
          const errorText = row.issues.map((i) => i.message).join(', ');

          return (
            <Stack
              direction="row"
              alignItems="center"
              data-test-id={buildTestId(
                TIMETABLE.INCONSISTENCIES_ROW_PREFIX,
                { name: String(row.rowIndex + 1) },
                'name',
              )}
              sx={{ py: 1.5, borderBottom: 1, borderColor: 'divider' }}
            >
              <Typography variant="h3" sx={COL_SX} color="common.grey2">
                {row.rowIndex + 1}
              </Typography>
              {columns.map((col) => {
                const issue = issuesByCol.get(col);
                return (
                  <TypographyWithOverflowHint
                    variant="h3"
                    key={col}
                    data-test-id={buildTestId(
                      TIMETABLE.INCONSISTENCIES_CELL_PREFIX,
                      { name: col },
                      'name',
                    )}
                    sx={{
                      ...getColSx(col),
                      color: issue ? getColor(issue) : 'common.grey2',
                    }}
                  >
                    {String(row.data[col]) || '-'}
                  </TypographyWithOverflowHint>
                );
              })}
              <TypographyWithOverflowHint
                variant="h3"
                color="common.grey2"
                sx={COL_ERROR_SX}
                data-test-id={TIMETABLE.INCONSISTENCIES_CELL_ERROR}
              >
                {errorText}
              </TypographyWithOverflowHint>
            </Stack>
          );
        }}
      />
    </Stack>
  );
};

const TabIcon = (type: Tab) => (
  <Icon
    sx={(theme) => ({
      mb: 0.5,
      color: theme.palette.background.paper,
      path: { color: type === Tab.Errors ? theme.palette.error.main : theme.palette.common.orange },
    })}
  >
    <Attention2Icon />
  </Icon>
);
